import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { ArrowRight, AlertTriangle, Ban, Clock, RefreshCw, Train as TrainIcon, Star, Loader2 } from 'lucide-react';
import { getComposition } from '../data/mock';
import { useFavorites } from '../hooks/useFavorites';
import { useStationResolver, useLiveBoard } from '../hooks/useLiveBoard';
import { useFluoVehicles } from '../hooks/useFluoVehicles';
import { useGrandEstStations } from '../hooks/useGrandEstStations';
import { Link } from 'react-router-dom';
import ShareTrain from './ShareTrain';
import WhatsAppChannelBadge from './WhatsAppChannelBadge';
import ExportPdfButton from './ExportPdfButton';
import DisruptionsBanner from './DisruptionsBanner';
import TypeChip from './TypeChip';

const PictoImg = ({ src, alt, sizeClass }) => {
  const [err, setErr] = React.useState(false);
  if (err) return <TrainIcon className={`${sizeClass} text-ter-blue/60`} />;
  return <img src={src} className={`${sizeClass} object-contain`} alt={alt} onError={() => setErr(true)} />;
};

const StatusPill = ({ status, tr }) => {
  if (status === 'cancelled') return <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase text-white bg-ter-red px-2 py-0.5 rounded"><Ban className="w-3 h-3" /> {tr('board.status_cancelled')}</span>;
  if (status === 'delayed') return <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase text-amber-900 bg-amber-200 px-2 py-0.5 rounded"><Clock className="w-3 h-3" /> {tr('board.status_delayed')}</span>;
  return <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase text-emerald-900 bg-emerald-100 px-2 py-0.5 rounded">{tr('board.status_ontime')}</span>;
};

const DepartureBoard = ({ stationName, mode = 'departures', stopAreaIdProp = null }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(mode);
  const [tick, setTick] = useState(0);
  const [provider, setProvider] = useState('navitia');
  const { isFavorite, toggleFavorite } = useFavorites();
  const { stopAreaId: resolvedId, resolving } = useStationResolver(stopAreaIdProp ? null : stationName);
  const stopAreaId = stopAreaIdProp || resolvedId;
  const { data: board, loading, error, mockFallback, reconnecting, reload } = useLiveBoard(stopAreaId, stationName);
  const { isFluoCar, getFluoCarSeats, getFluoPictoUrl, loaded: fluoLoaded } = useFluoVehicles();
  const { isGrandEst } = useGrandEstStations();

  useEffect(() => { setTab(mode); }, [mode]);
  useEffect(() => {
    fetch('/api/footer-config')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => { if (d.provider) setProvider(d.provider); })
      .catch(() => {});
  }, []);
  useEffect(() => {
    const id = setInterval(() => setTick(x => x + 1), 30000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const id = setInterval(() => reload(), 60000);
    return () => clearInterval(id);
  }, [reload]);

  const list = (board && (tab === 'departures' ? board.departures : board.arrivals)) || [];
  const now = useMemo(() => new Date(), [tick]);
  const fav = isFavorite(stationName);

  const getComp = (train) => {
    if (train.status === 'cancelled') return null;
    if (fluoLoaded && isFluoCar(train.number)) {
      return { wagonsCount: 1, totalSeats: getFluoCarSeats(train.number) };
    }
    if (train.wagonsCount > 0) return { wagonsCount: train.wagonsCount, totalSeats: train.totalSeats };
    return getComposition(train.number);
  };

  const getPicto = (train, sizeClass) => {
    const isFluo = (!train.type || train.type === 'TER')
      && (isGrandEst(train.origin) || isGrandEst(train.destination));
    if (fluoLoaded && isFluo) {
      const pictoUrl = getFluoPictoUrl(train.number);
      if (pictoUrl) {
        return <PictoImg src={pictoUrl} alt={isFluoCar(train.number) ? 'Car Fluo' : 'Train Fluo'} sizeClass={sizeClass} />;
      }
    }
    return <TrainIcon className={`${sizeClass} text-ter-blue/60`} />;
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden w-full max-w-full min-w-0">
      <div className="bg-ter-blue text-white px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center shrink-0">
            <TrainIcon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-white/70">{t('board.station_eyebrow')}</div>
            <div className="text-xl sm:text-2xl font-extrabold leading-tight truncate">{(board && board.station) || stationName}</div>
          </div>
          <button
            onClick={() => toggleFavorite(stationName)}
            className="ml-2 p-2 rounded-md hover:bg-white/10 transition-colors"
            title={t('board.favorite')}
          >
            <Star className={`w-5 h-5 ${fav ? 'fill-ter-yellow text-ter-yellow' : 'text-white/70'}`} />
          </button>
        </div>

        <div className="hidden md:flex items-center gap-3 text-sm">
          <div className="led-board px-3 py-1.5 rounded-md font-bold tabular-nums">
            {String(now.getHours()).padStart(2, '0')}:{String(now.getMinutes()).padStart(2, '0')}:{String(now.getSeconds()).padStart(2, '0')}
          </div>
          <ExportPdfButton
            stationName={(board && board.station) || stationName}
            mode={tab}
            list={list}
            sourceLabel={mockFallback ? t('board.source_demo') : provider === 'piv' ? t('board.source_piv') : t('board.source_live')}
          />
          <Button size="sm" variant="secondary" onClick={reload} className="bg-white/10 text-white hover:bg-white/20 border-0">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> {t('board.refresh')}
          </Button>
        </div>
      </div>

      {mockFallback && (
        <div className="px-4 sm:px-6 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900 flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            <span className="font-bold">{t('board.demo_banner')}</span> {error || ''}
          </span>
        </div>
      )}

      {!mockFallback && reconnecting && (
        <div className="px-4 sm:px-6 py-1.5 bg-slate-50 border-b border-slate-200 text-[11px] text-slate-600 flex items-center gap-2">
          <RefreshCw className="w-3 h-3 animate-spin" />
          <span>{t('board.reconnect_banner')}</span>
        </div>
      )}

      <div className="px-4 sm:px-6 pt-4 pb-2 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger
              value="departures"
              className="font-bold tab-fluo"
            >
              {t('board.tab_dep')}
            </TabsTrigger>
            <TabsTrigger
              value="arrivals"
              className="font-bold tab-fluo"
            >
              {t('board.tab_arr')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="text-xs text-slate-500">
          {mockFallback ? t('board.source_demo') : provider === 'piv' ? t('board.source_piv') : t('board.source_live')}
        </div>
      </div>

      {(loading || resolving) && (
        <div className="px-6 py-10 text-center text-slate-500 flex flex-col items-center gap-2">
          <Loader2 className="w-6 h-6 animate-spin text-ter-blue" />
          <span className="text-sm">{t('board.loading')}</span>
        </div>
      )}

      {!loading && !resolving && (() => {
        const tabDisruptions = (tab === 'departures'
          ? board?.departuresDisruptions
          : board?.arrivalsDisruptions) || [];
        if (tabDisruptions.length === 0) return null;
        return (
          <div className="px-4 sm:px-6 pt-4">
            <DisruptionsBanner disruptions={tabDisruptions} station={board.station || stationName} />
          </div>
        );
      })()}

      {!loading && !resolving && list.length === 0 && (
        <div className="px-6 py-10 text-center text-slate-500 text-sm">
          {tab === 'departures' ? t('board.empty_dep') : t('board.empty_arr')}
        </div>
      )}

      {!loading && !resolving && list.length > 0 && (
        <>
        {/* Mobile compact cards */}
        <div className="lg:hidden divide-y divide-slate-100" data-testid="board-mobile-cards">
          {list.map((train, idx) => {
            const comp = getComp(train);
            return (
              <div key={train.id || idx} className="px-4 py-3 slide-in">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <div className="font-mono font-extrabold text-lg text-ter-blue tabular-nums leading-none">
                        {train.status === 'cancelled' ? <span className="line-through text-slate-400">{train.time}</span> : train.time}
                      </div>
                      {train.delay > 0 && (
                        <span className="font-mono text-xs text-ter-red font-semibold tabular-nums">+{train.delay} • {train.realTime}</span>
                      )}
                      <TypeChip
                        type={train.type}
                        marque={train.marque}
                        modeTransport={train.modeTransport}
                        origin={train.origin}
                        destination={train.destination}
                        number={train.number}
                      />
                      <span className="font-mono text-xs text-slate-500 whitespace-nowrap shrink-0">{train.number}</span>
                    </div>
                    <div className="font-bold text-slate-900 mt-1.5 min-w-0 truncate">
                      {tab === 'departures' ? train.destination : train.origin}
                    </div>
                    {comp && (
                      <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1.5">
                        {getPicto(train, 'w-3.5 h-3.5')}
                        <span><strong className="text-slate-700 tabular-nums">{comp.wagonsCount}</strong> {t('board.col_coaches').toLowerCase()}</span>
                        <span>·</span>
                        <span><strong className="text-slate-700 tabular-nums">{comp.totalSeats}</strong> {t('board.col_seats').toLowerCase()}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    {isFluoCar(train.number) && !train.platform ? (
                      <span className="px-2 py-0.5 rounded bg-slate-100 font-semibold text-ter-blue text-sm">Bus</span>
                    ) : train.platform && train.platform !== '—' ? (
                      <span className="whitespace-nowrap inline-flex items-center gap-0.5 px-2 py-0.5 rounded bg-ter-blue text-white font-semibold text-sm">
                        {t('board.platform_label')}&thinsp;{train.platform}
                      </span>
                    ) : (
                      <span className="text-slate-300 text-xs whitespace-nowrap">{t('board.platform_label')} —</span>
                    )}
                    <StatusPill status={train.status} tr={t} />
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <WhatsAppChannelBadge station={(board && board.station) || stationName} train={train} />
                  <div className="ml-auto flex items-center gap-1">
                    <ShareTrain tab={tab} station={(board && board.station) || stationName} train={train} />
                    {train.vehicle_journey_id && (
                      <Link to={`/train/${encodeURIComponent(train.vehicle_journey_id)}?number=${encodeURIComponent(train.number)}&status=${encodeURIComponent(train.status || 'on_time')}&delay=${encodeURIComponent(train.delay || 0)}&marque=${encodeURIComponent(train.marque || '')}&modeTransport=${encodeURIComponent(train.modeTransport || '')}`} className="text-[11px] text-ter-blue font-semibold inline-flex items-center gap-1 hover:underline">
                        {t('board.view_route')} <ArrowRight className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop table */}
        <div className="hidden lg:block overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-4 sm:px-6 py-3 font-semibold">{t('board.col_time')}</th>
                <th className="px-2 py-3 font-semibold" colSpan={2}>{tab === 'departures' ? t('board.col_destination') : t('board.col_origin')}</th>
                <th className="px-2 py-3 font-semibold text-center">{t('board.col_coaches')}</th>
                <th className="px-2 py-3 font-semibold text-center">{t('board.col_seats')}</th>
                <th className="px-2 py-3 font-semibold text-center">{t('board.col_platform')}</th>
                <th className="px-2 sm:px-4 py-3 font-semibold text-right">{t('board.col_status')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((train, idx) => {
                const comp = getComp(train);
                return (
                <tr key={train.id || idx} className="border-t border-slate-100 hover:bg-slate-50 transition-colors slide-in">
                  <td className="px-4 sm:px-6 py-3 align-top">
                    <div className="train-time font-mono font-bold text-base text-ter-blue tabular-nums">
                      {train.status === 'cancelled' ? <span className="line-through text-slate-400">{train.time}</span> : train.time}
                    </div>
                    {train.delay > 0 && (
                      <div className="font-mono text-xs text-ter-red font-semibold tabular-nums">+{train.delay} • {train.realTime}</div>
                    )}
                  </td>
                  <td className="px-2 py-3 align-top" colSpan={2}>
                    <div className="flex items-start gap-2 min-w-0">
                      <div className="flex flex-col items-start gap-0.5 shrink-0">
                        <TypeChip
                          type={train.type}
                          marque={train.marque}
                          modeTransport={train.modeTransport}
                          origin={train.origin}
                          destination={train.destination}
                          number={train.number}
                        />
                        <span className="font-mono text-xs text-slate-500 whitespace-nowrap pl-1">{train.number}</span>
                      </div>
                      <div className="min-w-0 ml-2">
                        <span className="font-semibold truncate block">
                          {tab === 'departures' ? train.destination : train.origin}
                        </span>
                        <div className="flex items-center gap-2 mt-0.5">
                          {train.vehicle_journey_id && (
                            <Link to={`/train/${encodeURIComponent(train.vehicle_journey_id)}?number=${encodeURIComponent(train.number)}&status=${encodeURIComponent(train.status || 'on_time')}&delay=${encodeURIComponent(train.delay || 0)}&marque=${encodeURIComponent(train.marque || '')}&modeTransport=${encodeURIComponent(train.modeTransport || '')}`} className="text-xs text-ter-blue hover:underline whitespace-nowrap">
                              {t('board.see_detail')}
                            </Link>
                          )}
                          {train.has_disruption && (
                            <span title={train.disruptions?.[0]?.detail || t('disruptions.row_tooltip')} className="inline-flex items-center" data-testid={`row-disruption-${train.id || train.number}`}>
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-3 align-top text-center" data-testid={`coaches-${train.id || idx}`}>
                    {comp ? (
                      <span className="inline-flex items-center gap-1 text-slate-700 font-bold tabular-nums">
                        {getPicto(train, 'w-4 h-4')}
                        {comp.wagonsCount}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-2 py-3 align-top text-center" data-testid={`seats-${train.id || idx}`}>
                    {comp ? (
                      <span className="text-slate-700 font-bold tabular-nums">{comp.totalSeats}</span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-2 py-3 align-top text-center">
                    {isFluoCar(train.number) && !train.platform ? (
                      <span className="px-2 py-0.5 rounded bg-slate-100 font-semibold text-ter-blue text-sm">Bus</span>
                    ) : !train.platform || train.platform === '—' ? (
                      <span className="text-slate-300">—</span>
                    ) : (
                      <span className="whitespace-nowrap inline-flex items-center gap-0.5 px-2 py-0.5 rounded bg-ter-blue text-white font-semibold text-sm">
                        {t('board.platform_label')}&thinsp;{train.platform}
                      </span>
                    )}
                  </td>
                  <td className="px-2 sm:px-4 py-3 align-top text-right">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <StatusPill status={train.status} tr={t} />
                      <WhatsAppChannelBadge station={(board && board.station) || stationName} train={train} />
                      <ShareTrain tab={tab} station={(board && board.station) || stationName} train={train} />
                      {train.vehicle_journey_id && (
                        <Link to={`/train/${encodeURIComponent(train.vehicle_journey_id)}?number=${encodeURIComponent(train.number)}&status=${encodeURIComponent(train.status || 'on_time')}&delay=${encodeURIComponent(train.delay || 0)}&marque=${encodeURIComponent(train.marque || '')}&modeTransport=${encodeURIComponent(train.modeTransport || '')}`}>
                          <Button size="icon" variant="ghost" className="h-7 w-7">
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
      <div className="px-4 sm:px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center gap-3 text-xs text-slate-500">
        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
        {t('board.disclaimer')}
      </div>
    </div>
  );
};

export default DepartureBoard;
